import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { Disclosure } from "@headlessui/react";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { grid } from "ldrs";
import { FaQuestionCircle, FaUsers, FaUserCheck, FaSearch, FaFilter, FaChevronDown, FaChevronUp, FaTag, FaChartBar, FaIndustry, FaEye, FaEyeSlash, FaUserPlus, FaCheckCircle, FaExclamationCircle, FaTimes, FaStar } from 'react-icons/fa';
import DisclosureTopicTooltip from '../../../components/DisclosureTopicTooltip';

grid.register();

const ListQuestions = () => {
  const { t } = useTranslation();
  const [groupedTopics, setGroupedTopics] = useState({});
  const [filteredTopics, setFilteredTopics] = useState({});
  const [users, setUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [expandedTopics, setExpandedTopics] = useState({});
  const [allTopics, setAllTopics] = useState([]);
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [showDimensionsModal, setShowDimensionsModal] = useState(false);
  const [showDisclosureTopicsModal, setShowDisclosureTopicsModal] = useState(false);
  const [showAssignedModal, setShowAssignedModal] = useState(false);
  const [showUnassignedModal, setShowUnassignedModal] = useState(false);
  
  // Calculate stats dynamically based on current assignments
  const stats = useMemo(() => {
    const totalTopics = Object.keys(groupedTopics).length;
    const totalFields = allTopics.length;
    const assignedFields = allTopics.filter(topic => {
      // Use the same validation logic as getTopicAssignmentStats
      const assignedUserId = assignedUsers[topic.id];
      if (assignedUserId && assignedUserId !== "" && assignedUserId !== "0") {
        return true;
      }
      if (topic.prefilledAssignedUser) {
        const prefilledId = String(topic.prefilledAssignedUser);
        if (prefilledId !== "" && prefilledId !== "0" && prefilledId !== "null" && prefilledId !== "undefined") {
          return users.some(u => String(u.id) === prefilledId);
        }
      }
      return false;
    }).length;
    const unassignedFields = totalFields - assignedFields;
    
    return {
      totalTopics,
      totalFields,
      assignedFields,
      unassignedFields
    };
  }, [groupedTopics, allTopics, assignedUsers]);

  // Skeleton Components
  const SkeletonStatsCard = () => (
    <div className="bg-white rounded-lg shadow border border-gray-100 p-3 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
          <div className="h-6 bg-gray-200 rounded w-12 mb-1"></div>
          <div className="h-2 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full"></div>
      </div>
    </div>
  );

  const SkeletonTopicCard = () => (
    <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden animate-pulse">
      {/* Header Skeleton */}
      <div className="bg-gray-200 px-3 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1">
            <div className="w-6 h-6 bg-gray-300 rounded"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-300 rounded w-32 mb-1"></div>
              <div className="h-3 bg-gray-300 rounded w-16"></div>
            </div>
          </div>
          <div className="text-right">
            <div className="h-3 bg-gray-300 rounded w-12 mb-1"></div>
            <div className="h-2 bg-gray-300 rounded w-16"></div>
          </div>
        </div>
      </div>
      
      {/* Content Skeleton */}
      <div className="p-3">
        <div className="mb-3">
          <div className="h-3 bg-gray-200 rounded w-32 mb-1"></div>
          <div className="h-8 bg-gray-200 rounded w-full"></div>
        </div>
        
        <div className="h-8 bg-gray-200 rounded w-full mb-2"></div>
        
        <div className="space-y-1">
          <div className="h-16 bg-gray-100 rounded"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
        </div>
      </div>
    </div>
  );

  // Filter topics based on search term, unassigned filter, and user assignment filter
  useEffect(() => {
    let filtered = { ...groupedTopics };

    // Apply user assignment filter
    if (selectedUser) {
      const userFiltered = {};
      Object.entries(filtered).forEach(([topicName, topics]) => {
        let filteredTopics = topics;
        
        if (selectedUser === 'assigned') {
          // Show only assigned topics
          filteredTopics = topics.filter(topic => isTopicAssigned(topic));
        } else if (selectedUser === 'unassigned') {
          // Show only unassigned topics
          filteredTopics = topics.filter(topic => !isTopicAssigned(topic));
        } else {
          // Show only topics assigned to specific user
          const userIdStr = String(selectedUser);
          filteredTopics = topics.filter(topic => {
            const assignedId = assignedUsers[topic.id] || (topic.prefilledAssignedUser ? String(topic.prefilledAssignedUser) : null);
            return assignedId === userIdStr;
          });
        }
        
        if (filteredTopics.length > 0) {
          userFiltered[topicName] = filteredTopics;
        }
      });
      filtered = userFiltered;
    }

    // Apply unassigned filter (if not already filtered by user assignment)
    if (showUnassignedOnly && !selectedUser) {
      const unassignedFiltered = {};
      Object.entries(filtered).forEach(([topicName, topics]) => {
        const unassignedTopics = topics.filter(topic => !isTopicAssigned(topic));
        if (unassignedTopics.length > 0) {
          unassignedFiltered[topicName] = unassignedTopics;
        }
      });
      filtered = unassignedFiltered;
    }

    // Apply search term filter
    if (searchTerm) {
      const searchFiltered = {};
      Object.entries(filtered).forEach(([topicName, topics]) => {
        if (topicName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          topics.some(topic =>
            topic.heading.toLowerCase().includes(searchTerm.toLowerCase()) ||
            topic.standard.toLowerCase().includes(searchTerm.toLowerCase())
          )) {
          searchFiltered[topicName] = topics;
        }
      });
      filtered = searchFiltered;
    }

    setFilteredTopics(filtered);
  }, [searchTerm, groupedTopics, showUnassignedOnly, assignedUsers, selectedUser]);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const { data } = await axios.get('/questions');
        if (!data.status) throw new Error(data.error);

        // Handle case when there are no questions - ensure we have an array
        const topics = data.disclousreTopicHeadings || [];
        const usersList = data.users || [];

        const grouped = topics.reduce((acc, topic) => {
          const key = `${topic.disclosureTopic}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push(topic);
          return acc;
        }, {});

        Object.keys(grouped).forEach((key) => {
          grouped[key].sort(
            (a, b) =>
              (b.hasSection91 === true ? 1 : 0) - (a.hasSection91 === true ? 1 : 0)
          );
        });

        setGroupedTopics(grouped);
        setFilteredTopics(grouped);
        setUsers(usersList);
        setAllTopics(topics);
        
        // Initialize assignedUsers from prefilled assignments
        // Only add valid assignments (not empty, not "0", not null, and user exists)
        console.log('=== FRONTEND: Initializing assignments from API response ===');
        console.log('API Response topics with assignments:');
        const initialAssignments = {};
        topics.forEach(topic => {
          console.log(`Topic ${topic.id} (${topic.heading}): prefilledAssignedUser = ${topic.prefilledAssignedUser}`);
          if (topic.prefilledAssignedUser) {
            const prefilledId = String(topic.prefilledAssignedUser);
            console.log(`Processing prefilled ID: ${prefilledId}`);
            // Only add if it's a valid user ID and the user exists
            if (prefilledId !== "" && prefilledId !== "0" && prefilledId !== "null" && prefilledId !== "undefined") {
              const userExists = usersList.some(u => String(u.id) === prefilledId);
              console.log(`User ${prefilledId} exists in users list: ${userExists}`);
              if (userExists) {
                initialAssignments[topic.id] = prefilledId;
                console.log(`✓ Added assignment: topic ${topic.id} -> user ${prefilledId}`);
              } else {
                console.log(`✗ User ${prefilledId} not found in users list`);
              }
            } else {
              console.log(`✗ Skipping invalid prefilled ID: ${prefilledId}`);
            }
          } else {
            console.log(`No assignment for topic ${topic.id}`);
          }
        });
        console.log('Final initial assignments object:', initialAssignments);
        console.log('Number of assignments initialized:', Object.keys(initialAssignments).length);
        setAssignedUsers(initialAssignments);
      } catch (error) {
        console.error('Error fetching topics:', error);
        toastr.error(t('questions.failedToLoadTopics'));
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  const handleAssignUser = async (topicId, userId) => {
    try {
      // If userId is empty string, it means user selected "---SELECT USER---" (unassign)
      if (!userId || userId === "") {
        // Unassign the user - send empty/null to backend
        await axios.put('/questions', { disclosureTopicFieldId: topicId, assignedUserId: null });
        toastr.success(t('questions.userUnassigned'));
        // Remove from assignedUsers state
        setAssignedUsers((prev) => {
          const updated = { ...prev };
          delete updated[topicId];
          return updated;
        });
      } else {
        // Assign the user
        await axios.put('/questions', { disclosureTopicFieldId: topicId, assignedUserId: userId });
        toastr.success(t('questions.userAssigned'));
        // Convert to string to match select option values
        setAssignedUsers((prev) => ({ ...prev, [topicId]: String(userId) }));
      }
    } catch (error) {
      console.error('Error assigning user:', error);
      toastr.error(t('questions.failedToAssignUser'));
    }
  };

  const handleAssignWholeTopic = async (topicName, userId) => {
    try {
      const topicIds = groupedTopics[topicName].map(topic => topic.id);

      // Perform multiple API calls to assign each topic field ID individually
      await Promise.all(topicIds.map(topicId =>
        axios.put('/questions', { disclosureTopicFieldId: topicId, assignedUserId: userId })
      ));

      toastr.success(t('questions.disclosureTopicAssigned'));
      setAssignedUsers((prev) => {
        const updatedAssignments = { ...prev };
        // Convert to string to match select option values
        topicIds.forEach(id => updatedAssignments[id] = String(userId));
        return updatedAssignments;
      });
    } catch (error) {
      console.error('Error assigning Disclosure topic:', error);
      toastr.error(t('questions.failedToAssignTopic'));
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleTopicExpansion = (topicName) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicName]: !prev[topicName]
    }));
  };

  const getAssignmentStatus = (topicId) => {
    const assignedUserId = assignedUsers[topicId];
    if (assignedUserId) {
      const user = users.find(u => u.id === assignedUserId);
      return user ? user.name : t('users.unknownUser');
    }
    return null;
  };

  // Get unique sustainability dimensions
  const getUniqueDimensions = () => {
    const dimensions = new Set();
    allTopics.forEach(topic => {
      // Use sustainableDimensionName if available, otherwise try to extract from other fields
      if (topic.sustainableDimensionName) {
        dimensions.add(topic.sustainableDimensionName);
      } else if (topic.disclosureTopic) {
        // If no sustainableDimensionName, we can still show disclosure topics as a fallback
        // But ideally we should have sustainableDimensionName from backend
        dimensions.add(topic.disclosureTopic);
      }
    });
    return Array.from(dimensions).filter(d => d).sort();
  };

  // Get all assigned topics with user info
  const getAssignedTopics = () => {
    return allTopics.filter(topic => isTopicAssigned(topic)).map(topic => {
      const userId = assignedUsers[topic.id] || (topic.prefilledAssignedUser ? String(topic.prefilledAssignedUser) : null);
      const user = userId ? users.find(u => String(u.id) === String(userId)) : null;
      return {
        ...topic,
        assignedUser: user ? user.name : 'Unknown User',
        assignedUserId: userId
      };
    });
  };

  // Get all unassigned topics
  const getUnassignedTopics = () => {
    return allTopics.filter(topic => !isTopicAssigned(topic));
  };

  // Helper function to check if a topic is actually assigned to a valid user
  const isTopicAssigned = (topic) => {
    // Check assignedUsers state first
    const assignedUserId = assignedUsers[topic.id];
    if (assignedUserId && assignedUserId !== "" && assignedUserId !== "0") {
      return true;
    }
    
    // Check prefilledAssignedUser from API, but validate it's a valid user ID
    if (topic.prefilledAssignedUser) {
      const prefilledId = String(topic.prefilledAssignedUser);
      // Only consider it assigned if it's a valid non-empty, non-zero user ID
      if (prefilledId !== "" && prefilledId !== "0" && prefilledId !== "null" && prefilledId !== "undefined") {
        // Also verify the user actually exists in the users list
        const userExists = users.some(u => String(u.id) === prefilledId);
        return userExists;
      }
    }
    
    return false;
  };

    const getTopicAssignmentStats = (topics) => {
    const assigned = topics.filter(topic => isTopicAssigned(topic)).length;
    return { assigned, total: topics.length };
  };

  const getCommonAssignedUser = (topics) => {
    if (topics.length === 0) return "";
    
    // Get all assigned user IDs for this topic group (only valid assignments)
    const assignedUserIds = topics
      .filter(topic => isTopicAssigned(topic))
      .map(topic => {
        const assignedId = assignedUsers[topic.id];
        if (assignedId && assignedId !== "" && assignedId !== "0") {
          return String(assignedId);
        }
        if (topic.prefilledAssignedUser) {
          const prefilledId = String(topic.prefilledAssignedUser);
          if (prefilledId !== "" && prefilledId !== "0" && prefilledId !== "null" && prefilledId !== "undefined") {
            return prefilledId;
          }
        }
        return null;
      })
      .filter(id => id !== null && id !== "");

    // If no topics are assigned, return empty string
    if (assignedUserIds.length === 0) return "";

    // If not all topics are assigned, return empty string
    if (assignedUserIds.length !== topics.length) return "";

    // Check if all assigned user IDs are the same
    const firstUserId = assignedUserIds[0];
    const allSame = assignedUserIds.every(id => id === firstUserId);

    return allSame ? firstUserId : "";
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans antialiased">
      {/* Compact Header Section - Responsive */}
      <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 shadow-lg relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow">
                <FaQuestionCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{t('questions.sustainabilityDimensions')}</h1>
                <p className="text-green-100 text-xs sm:text-sm">{t('questions.assignESGTopics')}</p>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-sm text-transparent font-bold">{stats.totalTopics} {t('questions.totalTopics')}</p>
              <p className="text-xs text-green-100">{t('questions.lastUpdated')}: {new Date().toLocaleDateString()}</p>
              <p className="text-xs text-green-200 text-transparent">Showing: {Object.keys(filteredTopics).length} filtered</p>
            </div>
          </div>
        </div>
      </div>
  
      {/* Compact Stats Cards - Responsive */}
      <div className="max-w-full mx-auto px-3 sm:px-4 py-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {loading ? (
            <>
              <SkeletonStatsCard />
              <SkeletonStatsCard />
              <SkeletonStatsCard />
              <SkeletonStatsCard />
            </>
          ) : (
            <>
              {/* Sustainability Dimensions Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('questions.sustainabilityDimensionsCard')}</p>
                    <p className="text-lg sm:text-xl font-bold text-blue-600">{stats.totalTopics}</p>
                    <div className="text-xs text-gray-500">{t('questions.totalDimensions')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <FaTag className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                </div>
                <button
                  onClick={() => setShowDimensionsModal(true)}
                  className="w-full mt-2 px-2 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-semibold rounded transition-colors flex items-center justify-center gap-1"
                >
                  <FaEye className="w-3 h-3" />
                  {t('questions.view')}
                </button>
              </div>
      
              {/* Disclosure Topics Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('questions.disclosureTopics')}</p>
                    <p className="text-lg sm:text-xl font-bold text-purple-600">{stats.totalFields}</p>
                    <div className="text-xs text-gray-500">{t('questions.totalTopicsDesc')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <FaChartBar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                </div>
                <button
                  onClick={() => setShowDisclosureTopicsModal(true)}
                  className="w-full mt-2 px-2 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-600 text-xs font-semibold rounded transition-colors flex items-center justify-center gap-1"
                >
                  <FaEye className="w-3 h-3" />
                  {t('questions.view')}
                </button>
              </div>
      
              {/* Assigned Disclosure Topics Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('questions.assignedDisclosureTopics')}</p>
                    <p className="text-lg sm:text-xl font-bold text-green-600">{stats.assignedFields}</p>
                    <div className="text-xs text-gray-500">{t('questions.withUsers')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <FaUserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                </div>
                <button
                  onClick={() => setShowAssignedModal(true)}
                  className="w-full mt-2 px-2 py-1.5 bg-green-50 hover:bg-green-100 text-green-600 text-xs font-semibold rounded transition-colors flex items-center justify-center gap-1"
                >
                  <FaEye className="w-3 h-3" />
{t('questions.viewAssignment')}
                </button>
              </div>
      
              {/* Unassigned Disclosure Topics Card */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('questions.unassignedDisclosureTopics')}</p>
                    <p className="text-lg sm:text-xl font-bold text-orange-600">{stats.unassignedFields}</p>
                    <div className="text-xs text-gray-500">{t('questions.needAssignment')}</div>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <FaExclamationCircle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                  </div>
                </div>
                <button
                  onClick={() => setShowUnassignedModal(true)}
                  className="w-full mt-2 px-2 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-600 text-xs font-semibold rounded transition-colors flex items-center justify-center gap-1"
                >
                  <FaUserPlus className="w-3 h-3" />
{t('questions.assignPending')}
                </button>
              </div>
            </>
          )}
        </div>
  
        {/* Compact Search and Filter Section - Responsive */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-3 mb-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
            <div className="flex flex-col md:flex-row items-center gap-3 flex-1 w-full">
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <input
                  type="text"
                  placeholder={t('questions.searchTopicsPlaceholder')}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  value={searchTerm}
                  onChange={handleSearch}
                  autoComplete="off"
                />
              </div>
  
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${showFilters
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <FaFilter className="w-3 h-3" />
                  <span className="hidden sm:inline">{t('common.filter')}</span>
                </button>
              </div>
            </div>
          </div>
  
          {/* Advanced Filters - Responsive */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('questions.filterByUserAssignment')}</label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  >
                    <option value="">{t('questions.allAssignments')}</option>
                    <option value="assigned">{t('questions.assignedOnly')}</option>
                    <option value="unassigned">{t('questions.unassignedOnly')}</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                </div>
  
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSelectedUser('');
                      setSearchTerm('');
                      setShowUnassignedOnly(false);
                    }}
                    className="w-full px-2 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                  >
{t('questions.clearFilters')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
  
        {/* Compact Topics Grid - Responsive */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <SkeletonTopicCard />
            <SkeletonTopicCard />
            <SkeletonTopicCard />
            <SkeletonTopicCard />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {Object.entries(filteredTopics).map(([topicName, topics]) => {
              const topicStats = getTopicAssignmentStats(topics);
              const isExpanded = expandedTopics[topicName];
  
              return (
                <div key={topicName} className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden hover:shadow-md">
                  {/* Topic Header - Responsive */}
                  <div className="bg-gradient-to-r from-green-600 to-green-700 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-white/20 backdrop-blur-sm rounded flex items-center justify-center">
                          <FaIndustry className="w-3 h-3 text-white" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-white text-sm font-semibold truncate" title={topicName}>{t('questions.sustainabilityDimension')}: {topicName}</h3>
                          <p className="text-green-100 text-xs">{t('questions.disclosureTopicsLabel')}: {topics.length}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-white text-xs">
                          <span className="text-green-200">{topicStats.assigned}</span> / <span className="text-white">{topicStats.total}</span>
                        </div>
                        <div className="text-xs text-green-200">{t('questions.assignedLabel')}</div>
                      </div>
                    </div>
                  </div>
  
                  {/* Topic Content - Responsive */}
                  <div className="p-3">
                    {/* Bulk Assignment */}
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        <FaUserPlus className="inline w-2.5 h-2.5 mr-1" />
{t('questions.assignEntireTopic')}
                      </label>
                      <select
                        className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded focus:border-green-500 text-sm"
                        value={getCommonAssignedUser(topics)}
                        onChange={(e) => handleAssignWholeTopic(topicName, e.target.value)}
                      >
                        <option value="">{t('questions.selectUser')}</option>
                        {users.map((user) => (
                          <option key={user.id} value={String(user.id)}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
  
                    {/* Individual Fields */}
                    <div className="space-y-2">
                      <button
                        onClick={() => toggleTopicExpansion(topicName)}
                        className="w-full flex items-center justify-between px-2 py-1.5 bg-gray-100 hover:bg-gray-200 rounded transition-colors text-xs"
                      >
                        <span className="font-semibold text-gray-700">
{isExpanded ? t('questions.hide') : t('questions.viewIndividualFields')} ({topics.length})
                        </span>
                        {isExpanded ? (
                          <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                        ) : (
                          <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                        )}
                      </button>
  
                      {isExpanded && (
                        <div className="space-y-1">
                          {topics.map((topic) => {
                            const assignedUser = getAssignmentStatus(topic.id);
                            const isAssigned = isTopicAssigned(topic);
  
                            return (
                              <div key={topic.id} className="p-2 bg-gray-50 rounded border border-gray-200">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-1 gap-1">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-xs font-semibold text-gray-800 mb-0.5 flex items-center gap-1 min-w-0" title={topic.heading}>
                                      {topic.hasSection91 && (
                                        <span title={t('questionsManagement.section91Feature')} className="inline-flex flex-shrink-0">
                                          <FaStar className="w-3 h-3 text-amber-500" aria-hidden />
                                        </span>
                                      )}
                                      <span className="truncate">{topic.heading}</span>
                                    </h4>
                                    <p className="text-xs text-gray-600 mb-1 truncate" title={topic.standard}>{topic.standard}</p>
                                    {isAssigned && (
                                      <div className="flex items-center text-xs text-green-600">
                                        <FaCheckCircle className="w-2.5 h-2.5 mr-0.5 flex-shrink-0" />
                                        <span className="truncate">{t('questions.assignedTo')}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="sm:ml-2 self-start">
                                    {isAssigned ? (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800">
                                        <FaCheckCircle className="w-1.5 h-1.5 mr-0.5" />
{t('questions.assigned')}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-800">
                                        <FaExclamationCircle className="w-1.5 h-1.5 mr-0.5" />
{t('questions.unassigned')}
                                      </span>
                                    )}
                                  </div>
                                </div>
  
                                <select
                                  className="w-full px-1.5 py-1 bg-white border border-gray-300 rounded focus:border-green-500 text-xs"
                                  value={(() => {
                                    // Get the assigned user ID, prioritizing assignedUsers state
                                    const assignedId = assignedUsers[topic.id];
                                    if (assignedId && assignedId !== "" && assignedId !== "0") {
                                      return assignedId;
                                    }
                                    // Fall back to prefilledAssignedUser if valid
                                    if (topic.prefilledAssignedUser) {
                                      const prefilledId = String(topic.prefilledAssignedUser);
                                      if (prefilledId !== "" && prefilledId !== "0" && prefilledId !== "null" && prefilledId !== "undefined") {
                                        const userExists = users.some(u => String(u.id) === prefilledId);
                                        if (userExists) {
                                          return prefilledId;
                                        }
                                      }
                                    }
                                    // Return empty string to show "---SELECT USER---"
                                    return "";
                                  })()}
                                  onChange={(e) => handleAssignUser(topic.id, e.target.value)}
                                >
                                  <option value="">{t('questions.selectUserPlaceholder')}</option>
                                  {users.map((user) => (
                                    <option key={user.id} value={String(user.id)}>
                                      {user.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
  
        {/* Empty State */}
        {!loading && Object.keys(filteredTopics).length === 0 && (
          <div className="flex justify-center items-center py-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-3">
                <FaQuestionCircle className="w-6 h-6 text-white" />
              </div>
              <p className="text-gray-600 text-sm font-semibold mb-1">No topics found</p>
              <p className="text-gray-400 text-xs">Try adjusting your search criteria</p>
            </div>
          </div>
        )}

        {/* Filter Active Notice */}
        {showUnassignedOnly && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FaFilter className="w-4 h-4 text-orange-600 mr-2" />
                <span className="text-sm font-semibold text-orange-800">
                  {t('questions.showingOnlyUnassigned', { count: Object.values(filteredTopics).flat().length })}
                </span>
              </div>
              <button
                onClick={() => setShowUnassignedOnly(false)}
                className="text-xs text-orange-600 hover:text-orange-800 underline"
              >
                {t('questions.clearFilter')}
              </button>
            </div>
          </div>
        )}

        {/* Note at bottom */}
        {!loading && Object.keys(filteredTopics).length > 0 && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">{t('questions.clickCardsForDetails')}</p>
          </div>
        )}
      </div>

      {/* Sustainability Dimensions Modal */}
      {showDimensionsModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-3">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 flex items-center justify-between">
              <h3 className="text-white text-lg font-semibold flex items-center gap-2">
                <FaTag className="w-5 h-5" />
                {t('questions.sustainabilityDimensions')}
              </h3>
              <button
                onClick={() => setShowDimensionsModal(false)}
                className="bg-transparent hover:bg-white/10 text-white hover:text-gray-200 transition-colors rounded p-1"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="space-y-2">
                {getUniqueDimensions().map((dimension, index) => (
                  <div key={index} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-semibold text-blue-900">{dimension}</p>
                  </div>
                ))}
              </div>
              {getUniqueDimensions().length === 0 && (
                <p className="text-gray-500 text-center py-4">{t('questions.noSustainabilityDimensionsFound')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Disclosure Topics Modal */}
      {showDisclosureTopicsModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-3">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col relative">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-3 flex items-center justify-between">
              <h3 className="text-white text-lg font-semibold flex items-center gap-2">
                <FaChartBar className="w-5 h-5" />
                {t('questions.allDisclosureTopics')} ({allTopics.length})
              </h3>
              <button
                onClick={() => setShowDisclosureTopicsModal(false)}
                className="bg-transparent hover:bg-white/10 text-white hover:text-gray-200 transition-colors rounded p-1"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 overflow-x-visible">
              <div className="space-y-3">
                {allTopics.map((topic) => (
                  <div key={topic.id} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-purple-900 mb-1">{topic.heading}</p>
                        <p className="text-xs text-purple-700 mb-1">
                          <span className="font-medium">{t('questions.topicLabel')}:</span>{' '}
                          <DisclosureTopicTooltip 
                            disclosureTopic={topic.disclosureTopic}
                            disclosureDescription={topic.disclosureDescription}
                            className="text-purple-700"
                          />
                        </p>
                        {topic.standard && (
                          <p className="text-xs text-purple-600">
                            <span className="font-medium">{t('questions.standardLabel')}:</span> {topic.standard}
                          </p>
                        )}
                      </div>
                      {isTopicAssigned(topic) ? (
                        <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          {t('questions.assigned')}
                        </span>
                      ) : (
                        <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                          {t('questions.unassigned')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {allTopics.length === 0 && (
                <p className="text-gray-500 text-center py-4">{t('questions.noDisclosureTopicsFound')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assigned Disclosure Topics Modal */}
      {showAssignedModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-3">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col relative">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-3 flex items-center justify-between">
              <h3 className="text-white text-lg font-semibold flex items-center gap-2">
                <FaUserCheck className="w-5 h-5" />
                {t('questions.assignedDisclosureTopicsTitle')} ({getAssignedTopics().length})
              </h3>
              <button
                onClick={() => setShowAssignedModal(false)}
                className="bg-transparent hover:bg-white/10 text-white hover:text-gray-200 transition-colors rounded p-1"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 overflow-x-visible">
              <div className="space-y-3">
                {getAssignedTopics().map((topic) => (
                  <div key={topic.id} className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-green-900 mb-1">{topic.heading}</p>
                        <p className="text-xs text-green-700 mb-1">
                          <span className="font-medium">{t('questions.topicLabel')}:</span>{' '}
                          <DisclosureTopicTooltip 
                            disclosureTopic={topic.disclosureTopic}
                            disclosureDescription={topic.disclosureDescription}
                            className="text-green-700"
                          />
                        </p>
                        {topic.standard && (
                          <p className="text-xs text-green-600 mb-2">
                            <span className="font-medium">{t('questions.standardLabel')}:</span> {topic.standard}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <FaUserCheck className="w-3 h-3 text-green-600" />
                          <p className="text-xs font-medium text-green-800">
                            {t('questions.assignedToLabel')}: <span className="font-semibold">{topic.assignedUser}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {getAssignedTopics().length === 0 && (
                <p className="text-gray-500 text-center py-4">{t('questions.noAssignedTopics')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unassigned Disclosure Topics Modal */}
      {showUnassignedModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-3">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col relative">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-4 py-3 flex items-center justify-between">
              <h3 className="text-white text-lg font-semibold flex items-center gap-2">
                <FaExclamationCircle className="w-5 h-5" />
                {t('questions.unassignedDisclosureTopicsTitle')} ({getUnassignedTopics().length})
              </h3>
              <button
                onClick={() => setShowUnassignedModal(false)}
                className="bg-transparent hover:bg-white/10 text-white hover:text-gray-200 transition-colors rounded p-1"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 overflow-x-visible">
              <div className="space-y-3">
                {getUnassignedTopics().map((topic) => (
                  <div key={topic.id} className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-orange-900 mb-1">{topic.heading}</p>
                        <p className="text-xs text-orange-700 mb-1">
                          <span className="font-medium">{t('questions.topicLabel')}:</span>{' '}
                          <DisclosureTopicTooltip 
                            disclosureTopic={topic.disclosureTopic}
                            disclosureDescription={topic.disclosureDescription}
                            className="text-orange-700"
                          />
                        </p>
                        {topic.standard && (
                          <p className="text-xs text-orange-600">
                            <span className="font-medium">{t('questions.standardLabel')}:</span> {topic.standard}
                          </p>
                        )}
                      </div>
                      <div className="ml-3">
                        <select
                          className="px-2 py-1.5 bg-white border border-orange-300 rounded text-xs focus:border-orange-500 focus:outline-none"
                          value={assignedUsers[topic.id] || ""}
                          onChange={(e) => {
                            if (e.target.value) {
                              handleAssignUser(topic.id, e.target.value);
                              setShowUnassignedModal(false);
                            }
                          }}
                        >
                          <option value="">{t('questions.selectUserPlaceholder')}</option>
                          {users.map((user) => (
                            <option key={user.id} value={String(user.id)}>
                              {user.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {getUnassignedTopics().length === 0 && (
                <p className="text-gray-500 text-center py-4">{t('questions.noUnassignedTopics')}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListQuestions;